import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { jobListings as mockJobs } from '../mockData';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const BriefcaseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0M12 12.75h.008v.008H12v-.008Z" />
  </svg>
);

const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
  </svg>
);

const BookmarkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
  </svg>
);

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4 flex-shrink-0 translate-y-[2px]">
    <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

const ReferIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-4">
    <path d="M5.25 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM2.25 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM18.75 7.5a.75.75 0 0 0-1.5 0v2.25H15a.75.75 0 0 0 0 1.5h2.25v2.25a.75.75 0 0 0 1.5 0v-2.25H21a.75.75 0 0 0 0-1.5h-2.25V7.5Z" />
  </svg>
);

// Application form sidebar component
const ApplicationSidebar = ({ job, navigate }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    linkedinUrl: '',
    noLinkedin: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!formData.fullName || !formData.email || !formData.phone) {
      toast({ title: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backendUrl}/api/application-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: job.id || job.job_id,
          full_name: formData.fullName,
          email: formData.email,
          phone_country_code: '+1',
          phone_number: formData.phone,
          linkedin_url: formData.noLinkedin ? '' : formData.linkedinUrl,
          no_linkedin: formData.noLinkedin,
        }),
      });
      if (resp.ok) {
        toast({ title: 'Application started!', description: 'Redirecting to application flow...' });
        navigate(`/apply/${job.id || job.job_id}`);
      } else {
        const err = await resp.json();
        toast({ title: 'Error', description: err.detail || 'Failed to start application', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to submit. Please try again.', variant: 'destructive' });
    }
    setSubmitting(false);
  };

  return (
    <div className="w-full flex-col rounded-xl bg-white p-6 pt-4 shadow-md border">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3">
          <div className="w-full">
            <label className="mb-1 block text-left text-sm font-medium leading-6 text-black">
              Full name<span className="text-gray-900 ml-0.5">*</span>
            </label>
            <input
              className="w-full rounded-md border-0 px-3 py-1.5 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-[#0A3900] outline-none"
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            />
          </div>
          <div className="w-full">
            <label className="mb-1 block text-left text-sm font-medium leading-6 text-black">
              Email<span className="text-gray-900 ml-0.5">*</span>
            </label>
            <input
              className="w-full rounded-md border-0 px-3 py-1.5 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-[#0A3900] outline-none"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
        </div>
        <div className="w-full">
          <label className="mb-1 block text-left text-sm font-medium leading-6 text-black">
            Phone<span className="text-gray-900 ml-0.5">*</span>
          </label>
          <div className="flex items-center rounded-md ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-[#0A3900]">
            <div className="flex items-center gap-1 pl-3 pr-2 border-r border-gray-200">
              <span className="text-sm">🇺🇸</span>
              <span className="text-xs text-gray-500">▾</span>
              <span className="text-sm text-gray-700">+1</span>
            </div>
            <input
              className="flex-1 border-0 px-3 py-1.5 text-sm outline-none bg-transparent"
              type="tel"
              placeholder="Phone number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-left text-sm font-medium leading-6 text-gray-800">
            LinkedIn URL<span className="text-gray-900 ml-0.5">*</span>
          </label>
          <input
            className="w-full rounded-md border-0 px-3 py-1.5 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-[#0A3900] outline-none disabled:bg-gray-50"
            type="url"
            placeholder="https://www.linkedin.com/in/..."
            value={formData.linkedinUrl}
            onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })}
            disabled={formData.noLinkedin}
          />
          <div className="mt-2 flex items-center space-x-2">
            <input
              type="checkbox"
              id="no-linkedin"
              className="h-4 w-4 rounded border-gray-300 text-[#0A3900] focus:ring-[#0A3900]"
              checked={formData.noLinkedin}
              onChange={(e) => setFormData({ ...formData, noLinkedin: e.target.checked })}
            />
            <label htmlFor="no-linkedin" className="cursor-pointer text-sm text-gray-500">
              I don't have a LinkedIn
            </label>
          </div>
        </div>
      </div>
      <button
        className="mt-4 w-full rounded-md bg-[#0A3900] px-3 py-2 text-sm font-medium text-white hover:bg-[#F1F5E6]0 disabled:opacity-50"
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? 'Submitting...' : 'Start Application'}
      </button>
      <button
        className="mt-2 w-full rounded-md bg-[#E0E8D0] px-3 py-2 text-sm font-medium text-[#0A3900] hover:bg-[#B8C4AD]"
        onClick={() => navigate('/login')}
      >
        Sign in to existing account
      </button>
    </div>
  );
};

// Similar job card component
const SimilarJobCard = ({ job }) => {
  const navigate = useNavigate();
  const slug = (job.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <button
      className="group block w-full max-w-[380px] flex-shrink-0 rounded-md bg-white px-3 pb-2.5 pt-1 shadow ring-1 ring-gray-200 transition-all hover:bg-[#F1F5E6]/25 hover:shadow-md hover:ring-2 hover:ring-[#0A3900] text-left"
      onClick={() => navigate(`/jobs/${job.id}/${slug}`)}
      style={{ scrollSnapAlign: 'start' }}
    >
      <div className="relative p-1">
        <div className="flex h-8 items-center">
          <h2 className="line-clamp-1 break-all pe-2 font-medium">{job.title}</h2>
        </div>
        <p className="absolute -right-1.5 top-[21px] flex -translate-y-1/2 items-center gap-1 rounded-md bg-gray-50/50 px-4 py-1 text-sm text-[#0A3900] opacity-0 backdrop-blur-sm group-hover:opacity-100">
          Apply
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="size-3 -rotate-45">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </p>
        <p className="line-clamp-1 text-[15px] text-gray-600">
          ${job.payRange?.min || job.pay_range?.min || 0} - ${job.payRange?.max || job.pay_range?.max || 0} / hour
        </p>
        <div className="mt-6 flex items-center justify-between">
          {job.avatars && job.avatars.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex -space-x-1">
                {job.avatars.slice(0, 3).map((a, i) => (
                  <img key={i} alt="" className="size-6 rounded-full border-2 border-white object-cover" src={a} width="24" height="24" />
                ))}
              </div>
              <p className="text-xs text-gray-500">{job.hiredCount || job.applications_count || 0} hired this month</p>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

const MercorJobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appOpen, setAppOpen] = useState(true);
  const [similarJobs, setSimilarJobs] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getJobById(id);
        setJob(data);
      } catch {
        const found = mockJobs.find(j => j.id === id);
        setJob(found || null);
      } finally {
        setLoading(false);
      }
    };
    load();

    // Get similar jobs from mock data
    const others = mockJobs.filter(j => j.id !== id).slice(0, 6);
    setSimilarJobs(others);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-medium text-gray-900 mb-4">Job Not Found</h2>
          <button onClick={() => navigate('/explore')} className="text-[#0A3900] font-medium hover:opacity-50">
            ← View all opportunities
          </button>
        </div>
      </div>
    );
  }

  const pay = job.payRange || job.pay_range || { min: 0, max: 0 };
  const title = job.title || '';
  const location = job.location || 'Remote';
  const type = job.type || job.job_type || 'Contract';
  const hiredCount = job.hiredCount || job.applications_count || 0;
  const avatars = job.avatars || [];
  const companyName = job.companyName || job.company_name || 'Duty AI';
  const description = job.description || '';
  const requirements = job.requirements || [];
  const skills = job.skills || [];
  const referralBonus = job.referralBonus || 200;

  const applicationSteps = [
    { name: 'Resume', done: false },
    { name: 'Domain Expert Interview', done: false, core: true },
    { name: 'Work Authorization', done: false },
  ];

  return (
    <div className="min-h-screen bg-[#F9FBF5]">
      {/* Header */}
      <div className="sticky top-0 z-40 flex w-full flex-row items-center justify-between bg-white px-8 pt-[15.5px] pb-2 lg:px-8 lg:pr-7">
        <div className="flex flex-row items-center gap-3.5">
          <Link to="/explore" className="max-sm:hidden">
            <div className="h-[34px] w-[34px] bg-[#082900] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
          </Link>
          <Link to="/explore" className="-ml-1.5 flex flex-row items-center gap-1 sm:hidden">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
            </svg>
            <div className="text-[15px] font-medium max-[340px]:hidden">Browse</div>
          </Link>
        </div>
        <div className="flex flex-row items-center gap-3 text-sm">
          {user ? (
            <button className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium hover:bg-gray-50" onClick={() => navigate('/explore')}>
              Explore
            </button>
          ) : (
            <button className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium hover:bg-gray-50" onClick={() => navigate('/login')}>
              Sign in
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <article className="mx-auto flex h-full max-w-7xl flex-col px-4 sm:px-8 pb-32 transition-all duration-300 lg:pt-5 pt-8 sm:pt-16">
        <div className="flex h-full flex-row gap-12">
          {/* Left content */}
          <div className="w-full duration-300 md:w-[calc(100%-450px)]">
            {/* Back link */}
            <Link
              to="/explore"
              className="hidden sm:flex flex-row items-center gap-1 font-medium text-[#0A3900] transition-opacity duration-200 hover:opacity-50"
            >
              ← View all opportunities
            </Link>

            {/* Title + Pay */}
            <div className="mb-4 flex justify-between max-lg:gap-6 lg:pt-8">
              <div className="flex items-start justify-between w-full">
                <div>
                  <div className="flex flex-col items-start gap-1">
                    <h1 className="text-2xl font-medium tracking-tight text-gray-900">{title}</h1>
                    <h2 className="mt-0.5 text-xl font-medium sm:hidden">
                      ${pay.min}-${pay.max} / hr
                    </h2>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3.5 mb-2">
                    <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
                      <BriefcaseIcon />
                      Hourly contract
                    </div>
                    <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
                      <LocationIcon />
                      {location}
                    </div>
                    {avatars.length > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-1">
                          {avatars.slice(0, 3).map((a, i) => (
                            <img key={i} alt="" className="size-6 rounded-full border-2 border-white object-cover" src={a} width="24" height="24" />
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">{hiredCount} hired this month</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="-mt-1 flex flex-col items-end max-sm:hidden">
                  <h2 className="text-[26px] font-semibold">${pay.min}-${pay.max}</h2>
                  <div className="text-[14px] text-gray-500">per hour</div>
                </div>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Company */}
            <div className="flex flex-row items-center justify-between gap-3 py-3">
              <div className="flex flex-row items-center gap-3">
                <div className="h-11 w-11 rounded-full border bg-gray-50 p-2.5">
                  <div className="h-full w-full bg-[#082900] rounded-md flex items-center justify-center">
                    <span className="text-white font-bold text-xs">M</span>
                  </div>
                </div>
                <div>
                  <div className="font-medium">Posted by {companyName}</div>
                  <div className="text-sm text-gray-500">
                    <span className="hover:text-[#0A3900] hover:underline cursor-pointer">dutyai.com</span>
                  </div>
                </div>
              </div>
              <button className="rounded-md p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-[#0A3900]">
                <BookmarkIcon />
              </button>
            </div>

            <hr className="border-gray-200" />

            {/* Application Progress */}
            <div className="w-full select-none">
              <button
                className="flex w-full cursor-pointer select-none flex-col items-center justify-between"
                onClick={() => setAppOpen(!appOpen)}
              >
                <div className="flex w-full items-center justify-between">
                  <div className="mb-3 mt-6 flex items-center gap-2">
                    <h2 className="text-xl font-medium text-gray-900">Application</h2>
                    <span className="inline-flex select-none items-center whitespace-nowrap px-2 py-1 mx-1 text-xs rounded-xl font-normal bg-gray-50 text-gray-600">
                      Not started
                    </span>
                  </div>
                  <ChevronIcon open={appOpen} />
                </div>
                <div className="mb-2 flex w-full items-center justify-between text-sm text-gray-500">
                  <span>0 of 3 steps completed</span>
                  <span className="text-gray-500">0%</span>
                </div>
                <div className="h-2 w-full rounded-xl bg-gray-200">
                  <div className="h-full rounded-xl bg-[#0A3900]" style={{ width: '0%' }} />
                </div>
              </button>

              {appOpen && (
                <ul className="pt-1">
                  {applicationSteps.map((step, idx) => (
                    <li key={idx} className="my-4 flex items-center justify-between text-sm text-gray-500">
                      <p className="flex flex-col pr-2">
                        <span className="flex items-center gap-2 text-base font-medium text-gray-900">
                          {step.name}
                          {step.core && (
                            <span className="flex w-fit items-center whitespace-nowrap rounded-full border border-gray-400 px-[0.5em] py-[0.25em] text-[10px] font-normal leading-none text-gray-400">
                              CORE
                            </span>
                          )}
                        </span>
                        <span className="pr-5 text-gray-400">Not done</span>
                      </p>
                      <span className="flex h-6 w-6 min-w-6 items-center justify-center rounded-full border border-gray-300" />
                    </li>
                  ))}
                </ul>
              )}

              {appOpen && (
                <div className="flex items-start gap-x-1 text-gray-400 mt-2">
                  <InfoIcon />
                  <span className="text-sm">
                    All application steps are reused whenever another role requires the same step, so you never have to upload your resume or take the same interview twice
                  </span>
                </div>
              )}

              {!appOpen && (
                <div className="relative mr-auto mt-4 w-full translate-y-1">
                  <p className="flex gap-2 overflow-x-auto whitespace-nowrap pr-3 text-base font-medium text-gray-900 scrollbar-hide">
                    <span>Resume<span className="text-gray-400">&nbsp;&nbsp;|</span></span>
                    <span>Domain Expert Interview<span className="text-gray-400">&nbsp;&nbsp;|</span></span>
                    <span>Work Authorization</span>
                  </p>
                  <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent" />
                </div>
              )}
            </div>

            <hr className="mt-4 border-gray-200" />

            {/* About the Role */}
            <section className="mt-8">
              <div className="min-w-0 overflow-x-hidden break-words mt-6">
                <p className="mb-2 mt-2 text-base text-gray-600">
                  <strong className="font-medium text-black">About the Role</strong>
                </p>
                <p className="mb-2 mt-2 text-base text-gray-600">
                  Duty AI is seeking experienced <strong className="font-medium text-black">{title}</strong> to support a leading AI lab in advancing research and infrastructure for next-generation machine learning systems. {description}
                </p>

                {requirements.length > 0 && (
                  <>
                    <p className="mb-2 mt-4 text-base text-gray-600">
                      <strong className="font-medium text-black">Ideal Qualifications</strong>
                    </p>
                    <ul className="mb-4 list-disc pl-4 text-base text-gray-600">
                      {requirements.map((req, i) => (
                        <li key={i} className="mb-1 text-base text-gray-600">{req}</li>
                      ))}
                    </ul>
                  </>
                )}

                {skills.length > 0 && (
                  <>
                    <p className="mb-2 mt-4 text-base text-gray-600">
                      <strong className="font-medium text-black">Key Skills</strong>
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {skills.map((skill, i) => (
                        <span key={i} className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-sm font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                <p className="mb-2 mt-4 text-base text-gray-600">
                  <strong className="font-medium text-black">More About the Opportunity</strong>
                </p>
                <ul className="mb-4 list-disc pl-4 text-base text-gray-600">
                  <li className="mb-1">Expected workload: ~15 hours per week, with flexibility to scale up to 30+ hours</li>
                  <li className="mb-1">Project start date: immediately, lasting for around 3-4 weeks</li>
                </ul>

                <p className="mb-2 mt-4 text-base text-gray-600">
                  <strong className="font-medium text-black">Earn ${referralBonus} by referring</strong>
                </p>
                <p className="mb-2 mt-2 text-base text-gray-600">
                  Share the referral link below, and earn ${referralBonus} for each successful referral through this unique link. There's no limit on how many people you can refer.
                </p>
              </div>
            </section>

            {/* Contract and Payment Terms */}
            <section className="mt-8">
              <div className="min-w-0 overflow-x-hidden break-words mt-6">
                <h2 className="mb-3 mt-6 text-lg font-medium">Contract and Payment Terms</h2>
                <ul className="mb-4 list-disc pl-4 text-base text-gray-600">
                  <li className="mb-1">You will be engaged as an independent contractor.</li>
                  <li className="mb-1">This is a fully remote role that can be completed on your own schedule.</li>
                  <li className="mb-1">Projects can be extended, shortened, or concluded early depending on needs and performance.</li>
                  <li className="mb-1">Payments are weekly on Stripe or Wise based on services rendered.</li>
                </ul>
              </div>
              <div className="min-w-0 overflow-x-hidden break-words mt-6">
                <h2 className="mb-3 mt-6 text-lg font-medium">About Duty AI</h2>
                <p className="mb-2 mt-2 text-base text-gray-600">
                  Duty AI partners with leading AI labs and enterprises to train frontier models using human expertise. You will work on projects that focus on training and enhancing AI systems.
                </p>
              </div>
            </section>

            {/* Referral section */}
            <div className="mt-8">
              <div className="mb-3 flex flex-row items-center gap-1.5 text-xl font-medium lg:text-lg">
                <h2>Earn up to ${referralBonus} by referring</h2>
              </div>
              <div className="text-gray-500">
                Share the referral link below, and <span className="font-medium text-gray-700">earn up to ${referralBonus} for each successful referral</span> through this unique link. There's no limit on how many people you can refer.
              </div>
              <button className="mt-6 w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Activate my referral link
              </button>
            </div>

            <hr className="mt-8 border-gray-200" />

            {/* Similar Opportunities */}
            {similarJobs.length > 0 && (
              <div className="mt-6 w-full">
                <div className="flex w-full flex-row items-center justify-between">
                  <div className="text-lg font-medium">Similar Opportunities</div>
                </div>
                <div className="relative mx-auto mt-3 w-full">
                  <div className="flex gap-4 overflow-x-auto scroll-smooth px-2 py-2 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
                    {similarJobs.map((sJob) => (
                      <SimilarJobCard key={sJob.id} job={sJob} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <hr className="mt-8 border-gray-200" />

            {/* Bottom link */}
            <Link
              to="/explore"
              className="mt-6 hidden font-medium text-[#0A3900] transition-all duration-200 hover:opacity-50 md:block"
            >
              Explore all opportunities →
            </Link>
          </div>

          {/* Right sidebar - sticky application form */}
          <div className="sticky top-[-120px] h-fit w-[450px] pt-[154px] duration-300 max-md:hidden">
            <div className="hidden w-[420px] flex-col justify-end md:flex">
              <ApplicationSidebar job={job} navigate={navigate} />
              <button
                className="mt-4 flex w-full items-center justify-center gap-1 rounded-md border border-gray-200 bg-white py-2 text-base font-medium text-[#0A3900] shadow-md hover:bg-gray-50"
                onClick={() => {}}
              >
                <ReferIcon />
                <div>Earn up to ${referralBonus} by referring</div>
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Mobile bottom bar */}
      <div className="fixed bottom-0 z-30 flex h-[90px] w-full flex-row items-center justify-between border-t bg-white px-8 md:hidden">
        <div>
          <h3 className="line-clamp-1 text-lg font-medium">${pay.min}-${pay.max} / hr</h3>
          <p className="mt-[-0.5px] text-[15px] text-gray-500">Hourly contract · {location}</p>
        </div>
        <button
          className="ml-2 flex cursor-pointer items-center gap-3 rounded-md px-5 py-[11px] font-medium bg-[#0A3900] text-white shadow-sm hover:bg-[#F1F5E6]0 active:scale-95 duration-150"
          onClick={() => navigate(`/apply/${job.id || job.job_id}`)}
        >
          Apply Now
        </button>
      </div>
    </div>
  );
};

export default MercorJobDetailPage;
